import { useTheme } from "@mediaspace/shared/styled";
import styled from "@emotion/styled";
import { Button, Menu, MenuItem, menuClasses } from "@kaltura/ds-react-components";
import { Calendar24Icon } from "@kaltura/ds-react-icons";
import { useMediaQuery } from "@mediaspace/shared/utils";
import { FunctionComponent, useState, MouseEvent, useContext } from "react";
import { Breakpoint } from "@mui/system/createTheme/createBreakpoints";
import { iframeMenuProps } from "./helper";
import { ButtonClickAnalyticsType } from "@mediaspace/shared/types/ButtonClickAnalyticsType";
import { useButtonAnalytics } from "@mediaspace/hooks";
import AddToCalendarHelper from "@mediaspace/shared/utils/AddToCalendarHelper";
import { Config, ConfigContext } from "@mediaspace/shared/data-kms-config";

export enum Type {
    GOOGLE = 1,
    OUTLOOK = 2,
}

const StyledButton = styled(Button)({
    minWidth: "fit-content",
});

const StyledAddToCalendarButton = ({
    className,
    handleClick,
    addToCalendarActionText,
    size,
    collapseToIconBreakpoint,
    translucent,
    open,
}: {
    className?: string;
    handleClick: (event: MouseEvent<HTMLButtonElement>) => void;
    addToCalendarActionText: string;
    size?: "large" | "medium";
    collapseToIconBreakpoint: Breakpoint;
    translucent?: boolean;
    open?: boolean;
}) => {
    const theme = useTheme();
    const hideLabel = useMediaQuery(theme.breakpoints.down(collapseToIconBreakpoint));

    return (
        <StyledButton
            id={"addToCalendarToggleBtn"}
            className={className}
            variant={"borderless"}
            color={translucent ? "translucent" : "secondary"}
            aria-haspopup={true}
            aria-controls={"mediaCalendarToggleMenu"}
            aria-expanded={open}
            onClick={handleClick}
            startIcon={<Calendar24Icon />}
            size={size}
        >
            {!hideLabel && addToCalendarActionText}
        </StyledButton>
    );
};

const StyledMenu = styled(Menu)(({ theme }) => ({
    [`& .${menuClasses.paper}`]: {
        borderColor: "#333",
        backgroundColor: "#333",
    },
    [`& .${menuClasses.paper} li a`]: {
        color: theme.palette.common.white,
    },
    [`& .${menuClasses.paper} a:hover > div`]: {
        backgroundColor: "#444",
    },
    [`& .${menuClasses.paper} a:focus > div`]: {
        backgroundColor: "#444",
    },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
    color: theme.palette.common.white,
    "&:hover, &:focus, &:active, &:visited": {
        color: theme.palette.common.white,
    },
}));

export interface AddToCalendarTextProps {
    addToCalendarActionText: string;
    addToCalendarAddedText: string;
}

/**
 * add-to-calendar component
 * renders a dropdown + menu containing calendar options
 */
const AddToCalendar: FunctionComponent<{
    className?: string;
    items: { type: Type; label: string }[];
    sessionId: string;
    textProps: AddToCalendarTextProps;
    onMenuOpen?: () => void;
    size?: "large" | "medium";
    collapseToIconBreakpoint?: Breakpoint;
    translucent?: boolean;
}> = ({
    className,
    items,
    sessionId,
    textProps: { addToCalendarActionText, addToCalendarAddedText },
    onMenuOpen,
    size,
    collapseToIconBreakpoint = "xs",
    translucent,
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const config: Config = useContext(ConfigContext);
    const addToCalendarCallback = AddToCalendarHelper.getAddToCalendarAction(config);

    const sendButtonAnalytics = useButtonAnalytics();

    const handleButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
        onMenuOpen?.();
        setAnchorEl(event.currentTarget);
        sendAnalytics("Menu", ButtonClickAnalyticsType.MENU);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const sendAnalytics = (action: string, buttonType: number) => {
        sendButtonAnalytics(`Media Hero - Add to Calendar - ${action}`, buttonType);
    };

    return (
        <>
            <StyledAddToCalendarButton
                handleClick={handleButtonClick}
                addToCalendarActionText={addToCalendarActionText}
                className={className}
                size={size}
                collapseToIconBreakpoint={collapseToIconBreakpoint}
                translucent={translucent}
                open={open}
            />
            <StyledMenu
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                {...iframeMenuProps}
                id={"mediaCalendarToggleMenu"}
                MenuListProps={{
                    "aria-labelledby": "addToCalendarToggleBtn",
                }}
            >
                {items.map((item) => (
                    <StyledMenuItem
                        component="a"
                        onClick={() => {
                            addToCalendarCallback(sessionId, item.type === Type.GOOGLE ? "google" : "outlook");
                            sendAnalytics(
                                item.type === Type.GOOGLE ? "Google" : "Outlook",
                                ButtonClickAnalyticsType.SHARE
                            );
                            handleMenuClose();
                        }}
                        key={item.label}
                    >
                        {`${item.label} ${addToCalendarAddedText}`}
                    </StyledMenuItem>
                ))}
            </StyledMenu>
        </>
    );
};

export default AddToCalendar;
